import * as THREE from "three";
import { api } from "@/lib/api";

// RetroTrack-native room palette (kept on-brand: slate / blue / emerald / amber tones)
export const ROOM_TYPES = [
  { value: "living_room", label: "Living Room", color: "#2563EB" },
  { value: "bedroom", label: "Bedroom", color: "#7C3AED" },
  { value: "kitchen", label: "Kitchen", color: "#D97706" },
  { value: "bathroom", label: "Bathroom", color: "#0891B2" },
  { value: "hallway", label: "Hallway / Landing", color: "#64748B" },
  { value: "dining_room", label: "Dining Room", color: "#059669" },
  { value: "utility", label: "Utility Room", color: "#DB2777" },
  { value: "office", label: "Study / Office", color: "#4F46E5" },
  { value: "wc", label: "WC / Cloakroom", color: "#0D9488" },
  { value: "other", label: "Other", color: "#94A3B8" },
];

export const roomColor = (t) => (ROOM_TYPES.find((r) => r.value === t) || ROOM_TYPES[ROOM_TYPES.length - 1]).color;
export const roomLabel = (t) => (ROOM_TYPES.find((r) => r.value === t) || ROOM_TYPES[ROOM_TYPES.length - 1]).label;

const MARKER_COLORS = { damp: "#0891B2", mould: "#D97706", both: "#DC2626" };
export const markerColor = (t) => MARKER_COLORS[t] || "#DC2626";
export const markerGlyph = (t) => (t === "mould" ? "M" : t === "both" ? "B" : "D");

const hexA = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

export function planBounds(rooms) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  rooms.forEach((r) => {
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.w);
    maxY = Math.max(maxY, r.y + r.h);
  });
  if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 10; maxY = 8; }
  const center = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  return { minX, minY, maxX, maxY, center, w: maxX - minX, h: maxY - minY, maxDim: Math.max(maxX - minX, maxY - minY, 4) };
}

function fitView(rooms, W, H, margin) {
  const b = planBounds(rooms);
  const pw = Math.max(b.w, 1), ph = Math.max(b.h, 1);
  const ppm = Math.min((W - 2 * margin) / pw, (H - 2 * margin) / ph);
  const ox = margin - b.minX * ppm + ((W - 2 * margin) - pw * ppm) / 2;
  const oy = margin - b.minY * ppm + ((H - 2 * margin) - ph * ppm) / 2;
  return { ppm, ox, oy };
}

export function drawFloorPlan(ctx, rooms, view, opts = {}) {
  const { W, H, selectedId, markers = [], selectedMarkerId } = opts;
  const { ppm, ox, oy } = view;
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);

  // 1m grid, aligned to world origin
  const gm = ppm;
  ctx.lineWidth = 1;
  ctx.strokeStyle = "#eef2f7";
  if (gm > 6) {
    for (let gx = ((ox % gm) + gm) % gm; gx < W; gx += gm) {
      ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, H); ctx.stroke();
    }
    for (let gy = ((oy % gm) + gm) % gm; gy < H; gy += gm) {
      ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke();
    }
  }

  rooms.forEach((r) => {
    const x = ox + r.x * ppm, y = oy + r.y * ppm, w = r.w * ppm, h = r.h * ppm;
    const col = roomColor(r.room_type);
    ctx.fillStyle = hexA(col, r.id === selectedId ? 0.28 : 0.16);
    ctx.fillRect(x, y, w, h);
    ctx.lineWidth = r.id === selectedId ? 3 : 2;
    ctx.strokeStyle = col;
    ctx.strokeRect(x, y, w, h);

    ctx.textAlign = "center";
    ctx.fillStyle = "#0f172a";
    ctx.font = "bold 14px system-ui, sans-serif";
    ctx.fillText(r.name || roomLabel(r.room_type), x + w / 2, y + h / 2 - 1);
    ctx.font = "11px system-ui, sans-serif";
    ctx.fillStyle = "#475569";
    ctx.fillText(`${r.w}×${r.h}m · ${(r.w * r.h).toFixed(1)}m²`, x + w / 2, y + h / 2 + 15);

    if (r.id === selectedId) {
      ctx.fillStyle = "#ffffff";
      [[x, y], [x + w, y], [x, y + h], [x + w, y + h]].forEach(([hx, hy]) => {
        ctx.fillRect(hx - 5, hy - 5, 10, 10);
        ctx.strokeStyle = col;
        ctx.lineWidth = 2;
        ctx.strokeRect(hx - 5, hy - 5, 10, 10);
      });
    }
  });

  markers.forEach((m) => {
    const cx = ox + m.x * ppm, cy = oy + m.y * ppm;
    const col = markerColor(m.issue_type);
    const r = m.severity === "high" ? 12 : m.severity === "medium" ? 10 : 8;
    if (m.id === selectedMarkerId) {
      ctx.beginPath(); ctx.arc(cx, cy, r + 4, 0, Math.PI * 2);
      ctx.strokeStyle = col; ctx.lineWidth = 2; ctx.stroke();
    }
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = col; ctx.fill();
    ctx.lineWidth = 2.5; ctx.strokeStyle = "#ffffff"; ctx.stroke();
    ctx.fillStyle = "#ffffff"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.font = "bold 11px system-ui, sans-serif";
    ctx.fillText(markerGlyph(m.issue_type), cx, cy + 0.5);
    ctx.textBaseline = "alphabetic";
  });
}

export function render2DSnapshot(rooms, { width = 1000, height = 640, markers = [] } = {}) {
  const c = document.createElement("canvas");
  c.width = width; c.height = height;
  const ctx = c.getContext("2d");
  drawFloorPlan(ctx, rooms, fitView(rooms, width, height, 56), { W: width, H: height, markers });
  return c.toDataURL("image/jpeg", 0.9);
}

function buildScene(rooms, markers = []) {
  const b = planBounds(rooms);
  const scene = new THREE.Scene();
  scene.background = new THREE.Color("#eef2f7");
  scene.add(new THREE.AmbientLight(0xffffff, 0.78));
  const dl = new THREE.DirectionalLight(0xffffff, 0.85);
  dl.position.set(12, 20, 10);
  scene.add(dl);
  const span = Math.ceil(b.maxDim * 1.6);
  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(span, span),
    new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.9 })
  );
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -0.01;
  scene.add(floor);
  scene.add(new THREE.GridHelper(span, span, 0xcbd5e1, 0xe2e8f0));
  rooms.forEach((r) => {
    const w = r.w, d = r.h, hgt = r.wall_height || 2.4;
    const cx = r.x + w / 2 - b.center.x, cz = r.y + d / 2 - b.center.y;
    const geo = new THREE.BoxGeometry(w, hgt, d);
    const mesh = new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({ color: new THREE.Color(roomColor(r.room_type)), transparent: true, opacity: 0.78, roughness: 0.5, metalness: 0.05 })
    );
    mesh.position.set(cx, hgt / 2, cz);
    scene.add(mesh);
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geo), new THREE.LineBasicMaterial({ color: 0x1e293b }));
    edges.position.copy(mesh.position);
    scene.add(edges);
  });
  markers.forEach((m) => {
    const col = new THREE.Color(markerColor(m.issue_type));
    const rad = m.severity === "high" ? 0.32 : m.severity === "medium" ? 0.26 : 0.2;
    const ball = new THREE.Mesh(new THREE.SphereGeometry(rad, 16, 16), new THREE.MeshStandardMaterial({ color: col, emissive: col, emissiveIntensity: 0.35, roughness: 0.4 }));
    ball.position.set(m.x - b.center.x, 1.5, m.y - b.center.y);
    scene.add(ball);
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 1.5, 6), new THREE.MeshStandardMaterial({ color: col }));
    stem.position.set(m.x - b.center.x, 0.75, m.y - b.center.y);
    scene.add(stem);
  });
  return { scene, bounds: b };
}

export function render3DSnapshot(rooms, { width = 1000, height = 640, markers = [] } = {}) {
  const { scene, bounds } = buildScene(rooms, markers);
  const cam = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
  const dcam = Math.max(bounds.maxDim * 1.35, 7);
  cam.position.set(dcam, dcam * 0.95, dcam);
  cam.lookAt(0, 0, 0);
  const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
  renderer.setSize(width, height);
  renderer.render(scene, cam);
  const url = renderer.domElement.toDataURL("image/jpeg", 0.9);
  renderer.dispose();
  return url;
}

function dataUrlToBlob(dataUrl) {
  const [head, b64] = dataUrl.split(",");
  const mime = head.match(/:(.*?);/)[1];
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export async function uploadDataUrl(dataUrl, filename) {
  const fd = new FormData();
  fd.append("file", dataUrlToBlob(dataUrl), filename);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return { id: data.id };
}
